import { addMonths } from 'date-fns';
import { isEmpty } from 'lodash';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AlertError, LoaderWrapper, ToggleButton } from '@components/widgets';
import { useGetCurrentUser } from '@hooks/index';
import { SettingsTabsIDs } from '@pages/settings/Settings';
import DateInstance from '@services/date/date';
import { createCenteredPopup } from '@services/helper/helper';
import useBillingData from '@shared/hooks/payment/useBillingData';
import useKonnect from '@shared/hooks/payment/useKonnect';
import usePaymentInvoices from '@shared/hooks/payment/usePaymentInvoices';
import usePaymentPlans from '@shared/hooks/payment/usePaymentPlans';
import usePaymentRegistry from '@shared/hooks/payment/usePaymentRegistry';
import usePreventWindowClose from '@shared/hooks/payment/usePreventWindowClose';
import { PlanItemPayload } from '@shared/interfaces/backoffice';

import { removePercentage } from '@services/math/math';
import classNames from 'classnames';
import ProfilePlanItem from './item/ProfilePlanItem';
import './profilePlan.scss';

const ProfilePlan = () => {
    const [, setSearchParams] = useSearchParams({ tab: SettingsTabsIDs.plan });
    const {
        hasError,
        isLoading,
        plans
    }                         = usePaymentPlans({ fetchOnInit: true });

    const {
        hasError: paymentError,
        isLoading: paymentLoading,
        setIsLoading: setPaymentLoading,
        initPayment,
    }                             = useKonnect();
    const {
        isLoading: billingIsLoading,
        billingData,
        hasValidBillingData
    }                             = useBillingData();
    const {
        isLoading: upgradeLoading,
        upgradePaymentPlan
    }                             = usePaymentRegistry();
    const {
        isLoading: loadingInvoices,
        fetchInvoices,
        invoices
    }                             = usePaymentInvoices({ fetchOnInit: true });
    const user                    = useGetCurrentUser();
    const { triggerClosePrevent } = usePreventWindowClose();
    const [
        paymentErrorMessage,
        setPaymentErrorMessage
    ]                             = useState(null);
    const [isAnnual, setIsAnnual] = useState(false);
    const interval                = useRef(null);

    // const current            = plans?.length ? plans.find(plan => plan.default) : null;
    const isFirstOrder       = !isEmpty(invoices) && invoices?.data?.length === 0;
    const isValidBillingData = hasValidBillingData(billingData);

    const handleNewOrder   = (plan: PlanItemPayload, isFirstOrder = false) => {
        if (isLoading || paymentLoading || billingIsLoading || !isValidBillingData) {
            return;
        }

        triggerClosePrevent(true);

        let lastOrderDate = '';

        if (!isFirstOrder) {
            if (!invoices?.data?.length) {
                setPaymentErrorMessage('No previous invoice found, cannot renew plan.');

                return;
            }

            const lastOrder = invoices.data.at(-1);

            lastOrderDate       = lastOrder.endDate;

            if (lastOrder.status === 'pending') {
                setPaymentErrorMessage('Your last order is still pending, please wait until it is completed before renewing your plan.');

                return;
            }

            if (lastOrder.status === 'confirmed' && new Date(lastOrder.endDate) > new Date()) {
                setPaymentErrorMessage('Your current plan is still active, you cannot renew it now.');

                return;
            }
        }

        const startDate   = DateInstance.formatDate({
            date: DateInstance.newDate(lastOrderDate || new Date()),
            format: 'yyyy-MM-dd hh:mm'
        });
        const endDate     = DateInstance.formatDate({
            date: addMonths(DateInstance.newDate(), 1),
            format: 'yyyy-MM-dd hh:mm'
        });
        const invoiceDate = DateInstance.formatDate({
            date: DateInstance.newDate(),
            format: 'dd MMM yyyy, hh:mm'
        });
        const price       = isAnnual ? removePercentage(Number(plan.price), 20) : Number(plan.price);

        initPayment({
            amount: price * 100,
            email: user.email,
            firstName: user.firstname,
            lastName: user.lastname,
            phoneNumber: '-',
            webhook: `${window.location.origin}/payment-feedback?planId=${plan.id}&start=${startDate}&end=${endDate}&annual=${isAnnual}`,
            description: `
                Type: ${isFirstOrder ? 'Renewal payment' : 'Initial payment'} | 
                Date: ${invoiceDate} | 
                Site: ${user.site.website} | 
                Plan type: ${isAnnual ? 'Annually' : 'Monthly'}
            `,
        })
            .then(result => {
                const paymentWindow = createCenteredPopup({
                    url: result.payUrl,
                    title: 'Indepth Payment',
                    w: 700,
                    h: 800,
                });

                triggerClosePrevent(true, paymentWindow);

                // watch for popup window closing
                interval.current = setInterval(async () => {
                    if (paymentWindow.closed) {
                        clearInterval(interval.current);
                        setPaymentLoading(false);

                        fetchInvoices();

                        return;
                    }
                }, 500);
            })
            .catch(e => {
                console.log(e);
            });
    };

    const handleUpgrade = (plan: PlanItemPayload) => {
        if (isLoading || paymentLoading || billingIsLoading || !isValidBillingData) {
            return;
        }

        triggerClosePrevent(true);

        console.log(`Upgrading plan: ${plan.name}`);
        upgradePaymentPlan(plan.id).then(result => {
            console.log('upgradePaymentPlan:', result);
        });
    };

    return (
        <div className='profile-plan'>
            {isLoading || loadingInvoices || billingIsLoading && <LoaderWrapper />}

            {/* <div className='profile-plan-head'>
                <h2>Plan</h2>
                <p>Changes to these preferences will apply to future invoices only.</p>

                {!isValidBillingData && !billingIsLoading ? (
                    <AlertError
                        style={{ marginBottom: '2rem' }}
                        message="Please fill in your billing data to get started!"
                        link="Go to billing"
                        linkClick={() => setSearchParams({ tab: SettingsTabsIDs.billing })}
                    />
                ) : null}

                <h4>Current plan:</h4>

                {paymentError || paymentErrorMessage && <AlertError
                    style={{ marginBottom: '1rem' }}
                    message={paymentErrorMessage || 'An error occurred while processing your payment, please try again in a few minutes!'}
                />}
                {current ? <ProfilePlanItem
                    key={current.id}
                    isCurrent
                    showCurrentTag={false}
                    hideDescription
                    title={current.name}
                    subTitle={current.description}
                    description={current.longDescription}
                    price={current.price}
                    onClick={() => handleNewOrder(current, isFirstOrder)}
                /> : <NoData customStyle={{ alignItems: 'flex-start' }} message="No current plan available." />}
            </div> */}

            <div className='profile-plan-head'>
                <h2>Manage your plan</h2>
                <p>Changes to these preferences will apply to future invoices only.</p>
            </div>

            {!isValidBillingData && !(isLoading || loadingInvoices || billingIsLoading) && (
                <AlertError
                    message="Please fill in your billing data to get started!"
                    link="Go to billing"
                    linkClick={() => setSearchParams({ tab: SettingsTabsIDs.billing })}
                />
            )}

            {paymentError || paymentErrorMessage && <AlertError
                message={paymentErrorMessage || 'An error occurred while processing your payment, please try again in a few minutes!'}
            />}

            {plans?.length > 0 ? (
                <div className="profile-plan-toggler">
                    <h4 className={classNames({ active: !isAnnual })}>Monthly</h4>
                    <ToggleButton defaultValue={isAnnual} callback={setIsAnnual} />
                    <h4 className={classNames({ active: isAnnual })}>Annually (-20%)</h4>
                </div>
            ) : null}

            <div className="profile-plan-items">
                {plans?.length > 0 ? plans.map((plan) => {
                    const price = isAnnual ? removePercentage(Number(plan.price), 20) : plan.price;

                    return (
                        <ProfilePlanItem
                            key={plan.id}
                            title={plan.name}
                            isFirstOrder={isFirstOrder}
                            isCurrent={plan.default}
                            subTitle={plan.description}
                            description={plan.longDescription}
                            price={price}
                            isLoading={upgradeLoading}
                            // isAnnual={isAnnual}
                            isAnnual={false}
                            onClick={() => plan.default ? handleNewOrder(plan, isFirstOrder) : handleUpgrade(plan)}
                        />
                    );
                }) : null}

                {hasError && !isLoading ? <p>Error loading plans. Please try again later.</p> : null}
            </div>
        </div>
    );
};

export default ProfilePlan;
